import { createA11ySdk } from '../../../utils/velo/a11ySdk';
import { sanitizeHTML } from '../../../utils/sanitizeHTML';
import { LinkUtilsDefinition } from '@wix/viewer-service-link-utils/definition';
import type { CorvidSDKFactory } from '@wix/editor-elements-types/corvid';

import {
  wixGuard,
  endBlockTagRegex,
  endTagRegex,
  startTagRegex,
  wixCodeName as type,
} from '@wix/editor-elements-library/src/components/WRichText/constants';
import {
  flow,
  applyTransformationForGetHtml,
  applyTransformationForSetHtml,
  convertLinkProperties,
  removeWixGuard,
  decode,
  insertContentInHtml,
  escape,
  unescape,
  stripImpliedLinks,
} from '@wix/editor-elements-library/src/components/WRichText/corvid/utils';
import type { SetHTMLTransformConfig } from '@wix/editor-elements-library/src/components/WRichText/corvid/utils';
import {
  assert,
  createElementPropsSDKFactory,
} from '@wix/editor-elements-corvid-utils';
import { composeSDKFactories } from '../../../utils/velo/composeSdks';

const isUndefined = (str: string) => (assert.isNil(str) ? '' : str);
const endBlockTagPattern = new RegExp(endBlockTagRegex, 'mg');
const endTagPattern = new RegExp(endTagRegex, 'mg');
const startTagPattern = new RegExp(startTagRegex, 'mg');

const getText = (html: string) =>
  html
    ? decode(
        unescape(
          stripImpliedLinks(removeWixGuard(html))
            .replace(/\n/g, '')
            .replace(/<br>/g, '\n')
            .replace(/<br><\/br>/g, '\n')
            .replace(/<br\s*\/?>/g, '\n')
            .replace(endBlockTagPattern, '\n')
            .replace(endTagPattern, '')
            .replace(startTagPattern, '')
            .trim(),
        ),
      )
    : '';

export const WRICHTEXT_SDK_TYPE = type;

export const createWRichTextSdk: CorvidSDKFactory = api => {
  const { props, setProps, getService } = api;
  const linkUtils = getService(LinkUtilsDefinition);

  const convertLinksForSetter = (str: string) =>
    convertLinkProperties(str, linkUtils.getLinkProps);

  const convertLinksForGetter = (str: string) => {
    convertLinkProperties(str, linkUtils.getLinkProps, linkUtils.getLink);
    return str;
  };

  const getHtml = (html: string) =>
    flow(
      removeWixGuard,
      stripImpliedLinks,
      applyTransformationForGetHtml,
      convertLinksForGetter,
    )(html);

  const getPreparedHTML = (html: string, config?: SetHTMLTransformConfig) => {
    const flowParts = [
      isUndefined,
      (_html: string) => applyTransformationForSetHtml(_html, config),
      convertLinksForSetter,
      (_html: string) =>
        // @ts-expect-error TODO: Fix service usage
        linkUtils.getImpliedLinks(_html, false, { parseEscaped: true }),
      sanitizeHTML,
    ];

    return flow(...flowParts)(html);
  };

  return {
    get type() {
      return type;
    },
    get html() {
      return getHtml(props.richText?.html || '');
    },
    set html(value: string) {
      setProps({
        richText: {
          html: getPreparedHTML(value, { addDefaultClasses: false }),
        },
      });
    },
    get text() {
      return getText(props.richText.html);
    },
    set text(value: string) {
      const escapedHTML = value
        ? escape(value).replace(/\n/g, '<br>')
        : wixGuard;

      const html = linkUtils.getImpliedLinks(
        insertContentInHtml(
          stripImpliedLinks(props.richText.html),
          escapedHTML,
        ),
        false,
        // @ts-expect-error TODO: Fix service usage
        { parseEscaped: true },
      );

      setProps({
        richText: { html },
      });
    },
  };
};
const elementPropsSDKFactory = createElementPropsSDKFactory();

export default composeSDKFactories([
  createWRichTextSdk,
  elementPropsSDKFactory,
  createA11ySdk({ a11yProperty: 'a11y' }),
]);
